// ===========================================
// Report settings page
// ===========================================
// A page under the Reports section (NOT a tab inside System Settings), per
// Andrew 2026-09-16. Two tabs:
//   1. Overview  — which report cards render on the Reports Overview page.
//   2. Email reports — scheduled digests (Slice 2, not yet built).
//
// ⚠️ Reachable by anyone with `reports.read` — the Reports nav section's own
// gate. Saving requires `reports.write`, so a read-only staff user can view
// this page and is refused the save. That is intended, not an oversight.

import { useState } from 'react';
import { Box, Paper, Tab, Tabs } from '@mui/material';
import {
  Dashboard as OverviewIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import PageHeader from '../../../components/common/PageHeader';
import ReportsOverviewSettingsTab from '../../../components/admin/reports/ReportsOverviewSettingsTab';
import ReportSchedulesTab from '../../../components/admin/reports/ReportSchedulesTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-settings-tabpanel-${index}`}
      aria-labelledby={`report-settings-tab-${index}`}
      {...other}
    >
      {/* Body mounts only when selected, so a hidden tab issues no queries. */}
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `report-settings-tab-${index}`,
    'aria-controls': `report-settings-tabpanel-${index}`,
  };
}

export default function ReportSettingsPage() {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <PageHeader
        title="Report settings"
        subtitle="Choose which reports appear on the Overview, and configure emailed report digests"
        icon={<SettingsIcon />}
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Reports', path: '/admin/reports' },
          { label: 'Report settings' },
        ]}
      />

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<OverviewIcon />} iconPosition="start" label="Overview" {...a11yProps(0)} />
          <Tab icon={<EmailIcon />} iconPosition="start" label="Email reports" {...a11yProps(1)} />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <ReportsOverviewSettingsTab />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <ReportSchedulesTab />
      </TabPanel>
    </Box>
  );
}
